"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SUNMI_T3_INSTALL_PROFILE_ID = exports.DeviceBundleSchema = exports.DeviceModelSchema = exports.DeviceCapabilitiesSchema = exports.DEVICE_CAPABILITY_KEYS = exports.DEVICE_CAPABILITY_LEVELS = exports.DeviceEconomicsSchema = exports.DEVICE_TRACKING_MODES = exports.DEVICE_INTEGRATIONS = exports.SALE_MODES = exports.FORM_FACTOR_LABEL = exports.POS_BUILD_TARGETS = exports.SELECTABLE_TERMINAL_FORM_FACTORS = exports.TERMINAL_FORM_FACTORS = exports.DEVICE_LIFECYCLE_STATES = exports.DEVICE_CATEGORIES = void 0;
exports.isDualScreenFormFactor = isDualScreenFormFactor;
exports.buildTargetLabel = buildTargetLabel;
exports.buildTargetDevices = buildTargetDevices;
exports.formFactorLabel = formFactorLabel;
exports.integrationLabel = integrationLabel;
exports.landedCostCents = landedCostCents;
exports.bundleCost = bundleCost;
exports.marginCents = marginCents;
exports.marginPct = marginPct;
exports.termRevenueCents = termRevenueCents;
exports.paybackMonths = paybackMonths;
exports.needsTerminalId = needsTerminalId;
exports.deviceLane = deviceLane;
exports.isSunmiT3Model = isSunmiT3Model;
const zod_1 = require("zod");
const cardReaders_1 = require("./cardReaders");
/**
 * HARDWARE CATALOG — what a device IS and what it COSTS US.
 *
 * Cost lives here. PRICE NEVER DOES — that stays in the billing catalog
 * (billingCatalog.ts), which is the single definition of a price. Margin is
 * always derived from the two, never stored. See §4 / D2 of the hardware spec.
 */
/**
 * WHAT KIND OF THING THIS IS.
 *
 * Only `pos_terminal` carries behaviour: bundle costing picks the terminal in a
 * kit as the HOST and constrains every other component to its `worksWith` list
 * (`bundleCost` / `bundles.decorate`). Every other category is descriptive —
 * it drives an icon, a label and a filter, and nothing branches on it.
 *
 * That is why the three added 2026-08-31 (office supply, marketing material,
 * tools) are safe: they are non-terminal, so they behave exactly as
 * `peripheral` and `accessory` already do. Appended, never reordered — a stored
 * model names its category by value.
 */
exports.DEVICE_CATEGORIES = [
    'pos_terminal', 'payment_device', 'peripheral', 'accessory',
    'office_supply', 'marketing_material', 'tools',
];
/**
 * `draft` is NOT set from the lifecycle control — it is what "Save draft" in the
 * editor writes, and what "Publish device" clears. It stays in this union
 * because rows already carry it and every reader keys on it (a draft cannot sit
 * inside an active bundle, and only a draft may be hard-deleted).
 *
 * `pilot` is the state the lifecycle control offers in its place: a device that
 * is real and deployable but still under trial. NOTE that no reader gates on it
 * yet — to `bundles.decorate` and the deal builder a pilot behaves exactly like
 * `active`. It records intent today; enforcement is a separate change.
 */
exports.DEVICE_LIFECYCLE_STATES = ['draft', 'pilot', 'active', 'eol', 'discontinued'];
/**
 * SCREEN TYPE — a CLOSED list.
 *
 * It used to be open ("Add form factor"), and the result was two catalog rows
 * carrying `dual_screen`: a value provisioning does not recognise, which makes
 * `pos-terminal-provision` fall back to a plain `pos` terminal SILENTLY and
 * costs the device its customer-facing screen. An operator cannot be expected
 * to know that; the list is closed instead.
 *
 * `not_applicable` is a real answer, not a blank: a POS-category device that is
 * not a registerable terminal (a spare screen, a stand) has no screen type, and
 * saying so is different from leaving the question unanswered. It is
 * deliberately NOT a provisioning type — see `pos-terminal-provision`.
 *
 * SCREEN COUNT IS PART OF THE ANSWER (2026-09-18). A kiosk comes as one screen
 * or two, and `kiosk` alone could not say which — so a dual-screen kiosk came up
 * with no customer-facing screen, the same failure `dual_screen` used to cause.
 * `kiosk_single_screen` / `kiosk_dual_screen` replace it.
 *
 * `pos` and `kiosk` are DEPRECATED, NOT REMOVED, and must never be removed:
 * models already carry them, and `pos` is what `pos-terminal-provision` falls
 * back to for anything it does not recognise. They stay valid, stay rendered,
 * and are simply no longer offered for a NEW device — see
 * `SELECTABLE_TERMINAL_FORM_FACTORS`, which is what the editor draws.
 */
exports.TERMINAL_FORM_FACTORS = [
    'pos', 'kiosk', 'customer_display',
    'android_dual_screen', 'android_single_screen',
    'kiosk_single_screen', 'kiosk_dual_screen',
    'not_applicable',
];
/**
 * The screen types offered when ADDING a device, in the order they are drawn.
 *
 * Deliberately a subset of `TERMINAL_FORM_FACTORS`: 'pos' says nothing
 * `android_single_screen` does not say better, and bare 'kiosk' cannot express
 * its screen count. Both remain STORABLE and VALID — this list governs the
 * picker only, so an existing model keeps its answer and keeps rendering it.
 */
exports.SELECTABLE_TERMINAL_FORM_FACTORS = [
    'android_single_screen', 'android_dual_screen',
    'kiosk_single_screen', 'kiosk_dual_screen',
    'customer_display', 'not_applicable',
];
/**
 * Does this screen type drive a SECOND display?
 *
 * The one place that question is answered. Five separate `=== 'android_dual_screen'`
 * comparisons in the POS app each answered it privately, which is why adding
 * `kiosk_dual_screen` would otherwise have produced a dual-screen kiosk that
 * never presents on its second screen.
 */
function isDualScreenFormFactor(v) {
    return v === 'android_dual_screen' || v === 'kiosk_dual_screen';
}
exports.POS_BUILD_TARGETS = [
    {
        id: 'android_dual_screen',
        displayName: 'RUBY-ANDROID-POS-BUILD',
        supportedDevices: [
            'TC-TOUCH-K10',
            'TOUCH-DYNAMIC-VECTOR',
            'TC-TOUCH-A10',
            'Any Android POS',
        ],
    },
    {
        id: 'android_single_screen',
        displayName: 'RUBY-ANDROID-POS-SINGLE',
        supportedDevices: ['Any single-screen Android POS'],
    },
    {
        /* SUNMI T3 family (2026-10-29). The D3 Pro joins this build, not a new one
           (owner 2026-10-31: one till build for every SUNMI device; the till tells
           the machines apart by Build.MODEL). It carries the
           SUNMI-only SDKs (SUNBAY Taplink) the TC-Touch builds never do, and finds
           a customer screen itself — the catalog row is the base unit (single
           screen) and a CPad / customer display is a separate item. */
        id: 'android_sunmi_t3',
        displayName: 'RUBY-SUNMI-T3',
        supportedDevices: ['SUNMI T3 (80mm printer)', 'SUNMI T3 Pro', 'SUNMI D3 Pro'],
    },
];
/** The label for a build target id, or the raw id — never a blank. */
function buildTargetLabel(id) {
    if (!id)
        return '—';
    return exports.POS_BUILD_TARGETS.find((t) => t.id === id)?.displayName ?? id;
}
/** The devices a build runs on. Empty for an id no longer on the list. */
function buildTargetDevices(id) {
    if (!id)
        return [];
    return exports.POS_BUILD_TARGETS.find((t) => t.id === id)?.supportedDevices ?? [];
}
/**
 * TERMINAL TYPES, IN WORDS. Display only — the VALUES are load-bearing.
 *
 * `android_dual_screen` in particular is matched exactly by
 * `CustomerFacingTab` (which decides whether a register has a second screen),
 * by super-admin-ext2's provisioning `validTypes` (which SILENTLY falls back to
 * `'pos'` for anything it does not recognise), and by device-config-service,
 * where it is a DynamoDB partition key for branding assets. Rename the value
 * and every K10 quietly provisions as a plain terminal with no branding.
 *
 * Lives here, not in an app, because five separate `switch` statements across
 * super-admin-ui and platform-app were each returning their own version of
 * these four strings — which is how "Android Dual-Screen" and "Android
 * Dual-Screen POS Terminal" ended up on different screens for one device.
 *
 * `online_sales` is included: it is not a form factor an operator picks, but
 * super-admin-ext3 creates terminals with it and those rows have to render.
 */
exports.FORM_FACTOR_LABEL = {
    pos: 'POS Terminal',
    kiosk: 'Self-Service Kiosk',
    customer_display: 'Customer Display',
    android_dual_screen: 'Android Dual Screen POS Terminal',
    android_single_screen: 'Android Single Screen POS Terminal',
    kiosk_single_screen: 'Self-Service Kiosk — Single Screen',
    kiosk_dual_screen: 'Self-Service Kiosk — Dual Screen',
    not_applicable: 'Not Applicable',
    online_sales: 'Online Sales',
};
/** The label if there is one, otherwise the raw value — never a blank. */
function formFactorLabel(v) {
    if (!v)
        return '—';
    return exports.FORM_FACTOR_LABEL[v] ?? v;
}
exports.SALE_MODES = ['outright', 'installment', 'lease', 'rental', 'included'];
/**
 * WHAT A DEVICE HAS BUILT IN — "Integrated with".
 *
 * A STATIC list: `code` is what is stored and matched on, `label` is what a
 * person reads. The two are separate on purpose — the label can be reworded
 * ("Finger Print Scanner" → "Fingerprint reader") without rewriting stored rows,
 * which is exactly the mistake `dual_screen` taught: a value that doubles as a
 * display string cannot be renamed without breaking data.
 *
 * Adding an entry here is additive and safe. REMOVING one is not: a stored
 * device would keep a code nothing can name. Deprecate by dropping it from the
 * picker, never from this list.
 */
exports.DEVICE_INTEGRATIONS = [
    { code: 'barcode_scanner', label: 'Barcode Scanner' },
    { code: 'usb', label: 'USB' },
    { code: 'usb_c', label: 'USB-C' },
    { code: 'printer', label: 'Printer' },
    { code: 'scale', label: 'Scale' },
    { code: 'offline_sim', label: 'Offline Mode SIM' },
    { code: 'wifi', label: 'WiFi' },
    { code: 'ethernet', label: 'Ethernet' },
    { code: 'camera', label: 'Camera' },
    { code: 'fingerprint_scanner', label: 'Fingerprint Scanner' },
    { code: 'nfc', label: 'NFC' },
];
/** Label for a stored code; an unknown one shows as itself rather than vanishing. */
function integrationLabel(code) {
    return exports.DEVICE_INTEGRATIONS.find((i) => i.code === code)?.label ?? code;
}
// serialized-vs-quantity-tracking wave — how a model's stock is counted.
// 'serialized' (the default, absent = serialized so every model created
// before this field existed keeps today's behavior byte for byte): each
// physical unit is its own row (DeviceUnitSchema), individually tracked
// (RTA id, provisioning, timeline). 'quantity': no unit rows at all — stock
// is a DERIVED counter over a movement ledger (see StockMovementSchema in
// deviceOrders.ts) and every unit shares ONE barcode (`sharedBarcode`
// below). See bulk-vs-serialized-inventory.html screen 1 ("Track Item
// Individually" / "Track Total Inventory").
exports.DEVICE_TRACKING_MODES = ['serialized', 'quantity'];
exports.DeviceEconomicsSchema = zod_1.z.object({
    unitCostCents: zod_1.z.number().int().min(0),
    inboundShippingCents: zod_1.z.number().int().min(0).default(0),
    dutyCents: zod_1.z.number().int().min(0).default(0),
    defaultOutboundShippingCents: zod_1.z.number().int().min(0).default(0),
});
/**
 * WHAT A DEVICE PROFILE HAS — per peripheral, three answers, not two.
 *
 *   'builtin'  — ships in the unit. Absence on the bus is a FAULT.
 *   'external' — the unit takes one cabled on (USB / BT). Show it when present,
 *                never call it missing.
 *   'none'     — the profile has no such capability; features for it are hidden.
 *
 * Absent key = UNANSWERED (every model predates this) — readers fall back to
 * what the till can see for itself, never to 'none'.
 */
exports.DEVICE_CAPABILITY_LEVELS = ['builtin', 'external', 'none'];
exports.DEVICE_CAPABILITY_KEYS = ['printer', 'scanner', 'cardReader', 'fingerprint'];
const level = zod_1.z.enum(exports.DEVICE_CAPABILITY_LEVELS).optional();
exports.DeviceCapabilitiesSchema = zod_1.z.object({
    printer: level,
    scanner: level,
    cardReader: level,
    fingerprint: level,
});
exports.DeviceModelSchema = zod_1.z.object({
    modelId: zod_1.z.string().min(1),
    category: zod_1.z.enum(exports.DEVICE_CATEGORIES),
    subCategory: zod_1.z.string().optional(),
    manufacturer: zod_1.z.string().min(1),
    modelName: zod_1.z.string().min(1),
    description: zod_1.z.string().optional(),
    // Simple taxonomy for filtering + the white-label story (e.g. K10 ->
    // RubyTill-branded, A10 -> TC-Touch). Optional so every model created
    // before this field existed still validates. See inventory-financing-suite
    // mockup screen 7.
    brand: zod_1.z.string().optional(),
    // Units-in-stock threshold below which the model's inventory row should be
    // flagged low. Purely a UI/reporting signal — nothing here enforces it.
    reorderPoint: zod_1.z.number().int().min(0).optional(),
    /**
     * DOES THIS DEVICE NEED A TERMINAL ID BEFORE IT CAN SHIP?
     *
     * Absent means "decide from the category", which answers correctly for the
     * two obvious cases: a `payment_device` (a VP3350 card reader) needs one, a
     * `peripheral` or `accessory` (a printer, a scanner) never does. The flag
     * exists for the case the category cannot express — a `pos_terminal` with an
     * integrated reader, like a Dejavoo, is a payment device in every way that
     * matters to the dispatch gate but is not categorised as one.
     *
     * Getting this wrong in either direction is expensive: false, and an
     * unprogrammed terminal ships as a brick in a box; true on a printer, and
     * that printer waits for a TID that will never come. See `needsTerminalId`.
     */
    requiresTerminalId: zod_1.z.boolean().optional(),
    /**
     * IS THIS SOLD TO TENANTS AT ALL? (2026-08-30)
     *
     * Stock and sellability are different questions. A USB cable, a roll of
     * labels and a box of receipt paper are all inventory and none of them are
     * offered to a tenant — they are consumed internally. A terminal is both.
     *
     * Asked ONCE, at the end of creating a device: "stock only" finishes there,
     * and "sellable" continues into selling terms. It replaces the per-offer
     * `marketplace` checkbox that used to be buried on the financing wizard's
     * agreement step, which asked the same question far too late and once per
     * offer instead of once per device.
     *
     * ADDITIVE and OPTIONAL, and absent genuinely means UNANSWERED — every
     * device in the catalog predates it. Readers must not treat absent as
     * `true`: super-admin-ui resolves an unanswered device as sellable only if
     * it already carries offers, because defaulting them all to sellable would
     * flag every cable as misconfigured.
     */
    sellable: zod_1.z.boolean().optional(),
    // serialized-vs-quantity-tracking wave (additive). Absent = 'serialized' —
    // every model created before this field existed keeps its exact current
    // behavior. hardware-service REFUSES changing this once stock exists
    // (units for 'serialized', movement rows for 'quantity') — see models.ts's
    // upsertModel. 'Locks once stock exists' per the mockup's note.
    trackingMode: zod_1.z.enum(exports.DEVICE_TRACKING_MODES).optional(),
    // The ONE barcode every unit of a 'quantity' model shares (there is no
    // per-unit label to scan). Meaningless for 'serialized' models; not
    // cross-validated against trackingMode here (additive schema, in-service
    // enforcement only, same precedent as every other soft cross-field rule
    // in this file).
    sharedBarcode: zod_1.z.string().optional(),
    // Specs vary wildly by category (a cable has no Android version). Kept open
    // rather than forcing every accessory through a terminal-shaped schema.
    specs: zod_1.z.record(zod_1.z.any()).default({}),
    media: zod_1.z.object({
        heroImageUrl: zod_1.z.string().optional(),
        photos: zod_1.z.array(zod_1.z.string()).default([]),
        /**
         * SHORT DEMO VIDEO, additive (2026-08-31). Same bucket as the photos.
         *
         * A video is NEVER the hero: deal cards, order rows and bundle cards render
         * a still, so a video in `heroImageUrl` would render as nothing at all.
         * The hero stays a photo and the video is extra.
         */
        videos: zod_1.z.array(zod_1.z.string()).default([]),
        /** @deprecated Superseded by `documents` — a device needs more than one file. */
        datasheetUrl: zod_1.z.string().optional(),
    }).default({ photos: [], videos: [] }),
    /**
     * NAMED ATTACHMENTS — datasheets, certificates, wiring diagrams, manuals.
     *
     * The NAME is typed by the operator, not chosen from a list. A fixed set of
     * document types cannot survive contact with the next device: `datasheetUrl`
     * was exactly that mistake, one slot for a kind of file that turned out to
     * come in six kinds. Anything the presign accepts (PDF or image) can be
     * attached, any number of times.
     */
    documents: zod_1.z.array(zod_1.z.object({
        name: zod_1.z.string().trim().min(1),
        url: zod_1.z.string().min(1),
        contentType: zod_1.z.string().optional(),
        sizeBytes: zod_1.z.number().int().min(0).optional(),
        uploadedAt: zod_1.z.number().int().optional(),
    })).default([]),
    economics: exports.DeviceEconomicsSchema,
    vendor: zod_1.z.object({
        supplierName: zod_1.z.string().optional(),
        supplierSku: zod_1.z.string().optional(),
        leadTimeDays: zod_1.z.number().int().min(0).optional(),
        moq: zod_1.z.number().int().min(0).optional(),
    }).optional(),
    /**
     * The codes from `DEVICE_INTEGRATIONS` this device has.
     *
     * `string[]`, NOT `z.enum([...])`. The picker is closed, but the STORAGE is
     * permissive on purpose: super-admin-ui and hardware-service deploy
     * separately, so a UI that learns a new code first would write a value the
     * service's schema rejects — a 400 on save, for a checkbox. A wider store
     * accepts strictly more and no stored row can become invalid.
     */
    integrations: zod_1.z.array(zod_1.z.string().min(1)).default([]),
    appProfile: zod_1.z.object({
        deviceType: zod_1.z.string().optional(), // device-config-service branding key
        // Free-form (was z.enum(TERMINAL_FORM_FACTORS)): form factors are operator-
        // customizable — the DeviceEditor offers TERMINAL_FORM_FACTORS as the
        // built-in suggestions plus whatever other models already use, and lets a
        // new one be added. Widening enum -> string accepts strictly more, so no
        // stored row can become invalid. min(1) keeps '' from being persisted as a
        // phantom "form factor" — absence is expressed by omitting the field.
        terminalFormFactor: zod_1.z.string().min(1).optional(),
        otaChannel: zod_1.z.enum(['stable', 'canary']).optional(),
        minAppVersion: zod_1.z.string().optional(),
        /** Welcome-wizard steps for this model (WelcomeStepId[]); derived from capabilities when absent. */
        welcomeSteps: zod_1.z.array(zod_1.z.string()).optional(),
        /**
         * WHAT THE DEVICE CALLS ITSELF — Android `ro.product.model` values, e.g.
         * `TC-TOUCH-K10`. NEVER typed as a display name; read off the device.
         *
         * A LIST, not a string: one catalog model ships across firmware revisions
         * that report slightly different values, and a single string would make
         * every such unit read as the wrong device.
         *
         * Used to CHECK, never to decide: a unit reporting a model that matches no
         * catalog row, or a row other than the one it is registered as, is flagged
         * for a human. Empty or absent simply means nothing to check against.
         */
        hardwareIds: zod_1.z.array(zod_1.z.string().min(1).max(128)).optional(),
        /**
         * CAN THIS DEVICE TAKE A CUSTOM BOOT ANIMATION?
         *
         * `DeviceBrandingPlugin` installs one by writing `/oem/media/bootanimation.zip`
         * as root. That works on an AOSP terminal like the K10 and CANNOT work on a
         * Google-certified (GMS) device — verified boot, no `su`, no `/oem`. The
         * install already fails safe there, but without this flag the device sits in
         * the fleet as a permanently unbranded terminal and somebody chases it.
         *
         * Absent means UNANSWERED (every model predates this), which readers must
         * treat as "assume it can, as today" — not as false.
         */
        supportsBootAnimation: zod_1.z.boolean().optional(),
        /**
         * The profile's peripherals. Drives what the till SHOWS (top-bar chips,
         * fingerprint / card-reader features). Never gates which release a till
         * receives — every till on a build target gets every release.
         */
        capabilities: exports.DeviceCapabilitiesSchema.optional(),
        /**
         * Which installer hardware profile (`device-profiles.json` id: 'k10' |
         * 'a10' | 'vector') units of this model are. Pre-selects the install
         * panel's "Device profile". Free string so a new installer profile needs
         * no schema release.
         */
        installProfileId: zod_1.z.string().min(1).max(64).optional(),
    }).optional(),
    /**
     * WHICH PAYMENT CONFIG THIS DEVICE REGISTERS ON — asked once, when the
     * device is added, and required for category 'payment_device'.
     *
     * This replaces resolving routing from the model NAME. Name matching
     * (`EMV_DEVICE_MODELS`) survives only as a fallback for rows that predate the
     * picker: it is an EXACT match, so a catalog entry called "Dejavoo P17
     * Android Pin Pad" matched nothing and silently defaulted to NMI Cloud. The
     * ids here are the old `deviceClass` strings on purpose — already written on
     * every emv-device row and carried into reporting — so only labels are new.
     *
     * `'none'` is an ANSWER, not an absence: the device is stocked, ordered and
     * shipped normally, and registration REFUSES rather than guessing a lane.
     * Absent entirely means nobody was ever asked, which is a different thing.
     *
     * OPTIONAL at the schema level and always will be — every row in the catalog
     * predates it, and a required field with no default fails the shared-types
     * breaking-change check. The device editor enforces it for new payment
     * devices; see `resolvePaymentConfig`.
     */
    paymentConfigId: zod_1.z.enum([
        'none', 'dejavoo_spin_usb', 'dejavoo_spin_cloud', 'dejavoo_rubytill_pay', 'dejavoo_cloud', 'cloud', 'nmi_direct_vp3350',
        'magtek_usb', 'sunbay_tapro',
    ]).optional(),
    /**
     * EVERY config this model may be registered as — see `paymentConfigOptionsFor`.
     *
     * A Dejavoo P17 is `['dejavoo_spin_usb', 'dejavoo_spin_cloud']`: one device,
     * two routes, and which one it takes is decided per unit at registration.
     * Absent means "only `paymentConfigId`", which is every row written before
     * this field, so nothing changes for them.
     */
    paymentConfigOptions: zod_1.z.array(zod_1.z.enum([
        'none', 'dejavoo_spin_usb', 'dejavoo_spin_cloud', 'dejavoo_rubytill_pay', 'dejavoo_cloud', 'cloud', 'nmi_direct_vp3350',
        'magtek_usb', 'sunbay_tapro',
    ])).optional(),
    /**
     * How a PAYMENT DEVICE actually routes a card transaction. Only meaningful
     * for category 'payment_device'.
     *
     * DERIVED from `paymentConfigId` whenever the picker sets one, and kept
     * written so every existing reader (RegisterPoiModal, the seed script,
     * reporting) keeps working untouched. Read it through
     * `resolvePaymentConfig`, never on its own.
     */
    paymentProfile: zod_1.z.object({
        deviceClass: zod_1.z.enum(['cloud', 'nmi_direct_vp3350', 'dejavoo_spin_usb', 'dejavoo_spin_cloud', 'dejavoo_cloud', 'dejavoo_rubytill_pay', 'magtek_usb', 'sunbay_tapro']),
        connectionType: zod_1.z.enum(['cloud', 'bluetooth', 'usb']),
        creditMethod: zod_1.z.enum(['credit_nmi_cloud', 'credit_nmi_direct', 'credit_dejavoo', 'credit_dejavoo_cloud', 'credit_dejavoo_rtpay', 'credit_magtek', 'credit_sunbay']),
        emvMode: zod_1.z.enum(['cloud', 'sdk', 'usb', 'spin_cloud', 'rtpay', 'magtek', 'sunbay']),
    }).optional(),
    /**
     * WHICH BUILD EVERY UNIT OF THIS MODEL INSTALLS — a build-target id, e.g.
     * 'android_dual_screen'.
     *
     * PROMOTED FROM A HINT TO THE ANSWER (2026-09-18). It used to be advisory,
     * displayed beside a dropdown the operator still had to fill in by hand, on
     * the reasoning that catalog rows are entered by people whose data we do not
     * verify. That reasoning held while there was exactly ONE terminal model: the
     * only build on the list was the right one. With two models it inverts —
     * hand-picking from a list that names no device is a coin toss, and its wrong
     * side flashes the wrong OS onto a customer's till and is not noticed until
     * the machine misbehaves in a shop. The answer is now given ONCE, on the
     * device, by someone looking at the device, and the install panel resolves it
     * instead of asking. Deviating from it needs the unit override code — a
     * stronger gate than the free choice it replaces.
     *
     * THE SINGLE SOURCE OF TRUTH for the model→build link. The POS repo does NOT
     * carry the reverse list: the catalog is edited in a browser and gains a
     * device every few weeks, while `build-targets.json` needs a commit, a PR and
     * a deploy and gains one maybe yearly. Two lists would drift, so the
     * fast-moving one wins.
     *
     * A build target names a SOFTWARE CONTRACT, never a device — a TCANG K10 and
     * a Touch Dynamic Vector are ONE target and TWO models. Fork a target only
     * when the APK BYTES must differ (a vendor SDK, a different minSdk), never
     * for screen size, peripherals or payment lane, all of which are already
     * answered per-model or per-register.
     *
     * Still OPTIONAL, and absence still never blocks receiving stock or creating
     * a unit — but it now means UNANSWERED rather than "pick one at install",
     * and install refuses rather than guessing. Only meaningful for category
     * 'pos_terminal': a card reader is a peripheral of a terminal, not something
     * an APK is installed onto.
     *
     * NULLISH, not merely optional, and that is load-bearing. `upsertModel` merges
     * the request over the stored row, and JSON drops an `undefined` key on the
     * wire — so an editor that "cleared" this field by sending `undefined` would
     * send nothing at all and the old build would survive the save in silence.
     * Clearing it sends an explicit `null`. Every reader treats null and absent
     * the same (both are falsy), so nothing downstream has to learn a third state.
     */
    buildTarget: zod_1.z.string().min(1).max(64).nullish(),
    /**
     * CARD READER CAPABILITIES — only meaningful for a 'payment_device' row
     * (sunmi-card-setup.md §1). Overrides `DEFAULT_READER_CAPABILITIES[kind]`
     * key by key (`capabilitiesOf`). Optional and additive: absent = the kind's
     * default. Nullish so an editor can clear it with an explicit null.
     */
    readerCapabilities: cardReaders_1.ReaderCapabilitiesOverrideSchema.nullish(),
    /**
     * CARD SETUP POLICY — only meaningful for a 'pos_terminal' row
     * (sunmi-card-setup.md §2). A model that carries a VALID policy runs the
     * primary + backup card setup (`isCardSetupModel`); every other model is
     * untouched. Optional and additive; nullish so it can be cleared.
     */
    cardSetupPolicy: cardReaders_1.CardSetupPolicySchema.nullish(),
    compatibility: zod_1.z.object({
        worksWith: zod_1.z.array(zod_1.z.string()).default([]),
        requires: zod_1.z.array(zod_1.z.string()).default([]),
    }).default({ worksWith: [], requires: [] }),
    lifecycle: zod_1.z.object({
        status: zod_1.z.enum(exports.DEVICE_LIFECYCLE_STATES),
        eolDate: zod_1.z.number().optional(),
        replacedByModelId: zod_1.z.string().optional(),
        warrantyMonths: zod_1.z.number().int().min(0).optional(),
    }),
    displayOrder: zod_1.z.number().int().default(100),
    createdAt: zod_1.z.number(),
    updatedAt: zod_1.z.number(),
}).refine((m) => (m.lifecycle.status !== 'eol' && m.lifecycle.status !== 'discontinued')
    || !!m.lifecycle.replacedByModelId, { message: 'replacedByModelId is required when a model is retired', path: ['lifecycle', 'replacedByModelId'] });
exports.DeviceBundleSchema = zod_1.z.object({
    bundleId: zod_1.z.string().min(1),
    name: zod_1.z.string().min(1),
    description: zod_1.z.string().default(''),
    heroImageUrl: zod_1.z.string().optional(),
    // Every uploaded photo, not just the hero. Without this the gallery would
    // silently discard the non-hero images on save. Optional, so bundles created
    // before it still validate.
    photos: zod_1.z.array(zod_1.z.string()).default([]),
    components: zod_1.z.array(zod_1.z.object({
        modelId: zod_1.z.string().min(1),
        quantity: zod_1.z.number().int().positive(),
    })).min(1),
    combinedOutboundShippingCents: zod_1.z.number().int().min(0).default(0),
    /**
     * ADDITIVE: `pilot` (2026-09-01) — a kit in trial with selected tenants,
     * matching the device lifecycle's own pilot state. Every existing value is
     * unchanged, so rows written before this still parse.
     */
    status: zod_1.z.enum(['draft', 'pilot', 'active', 'eol']),
    displayOrder: zod_1.z.number().int().default(100),
    createdAt: zod_1.z.number(),
    updatedAt: zod_1.z.number(),
});
/** What one unit costs us to have in hand. Outbound shipping is per SHIPMENT, so it is excluded. */
function landedCostCents(e) {
    return e.unitCostCents + e.inboundShippingCents + e.dutyCents;
}
function bundleCost(bundle, modelsById) {
    let componentsLandedCents = 0;
    const missing = [];
    for (const c of bundle.components) {
        const m = modelsById[c.modelId];
        if (!m) {
            missing.push(c.modelId);
            continue;
        }
        componentsLandedCents += landedCostCents(m.economics) * c.quantity;
    }
    return {
        componentsLandedCents,
        totalCostCents: componentsLandedCents + (bundle.combinedOutboundShippingCents || 0),
        missing,
    };
}
function marginCents(priceCents, totalCostCents) {
    return priceCents - totalCostCents;
}
/** Null when price is zero — a margin percentage of nothing is meaningless, not 0% or -Infinity. */
function marginPct(priceCents, totalCostCents) {
    if (priceCents <= 0)
        return null;
    return (priceCents - totalCostCents) / priceCents;
}
/** Total the customer pays across a term. Outright / included are single payments. */
function termRevenueCents(sku) {
    if (sku.saleMode === 'outright' || sku.saleMode === 'included')
        return sku.amountCents;
    return sku.amountCents * (sku.termMonths ?? 1);
}
/**
 * Whole months of rental before cost is covered. CEILING, not division:
 * 9 x $79 = $711 does not cover $715.60, so the answer is 10.
 */
function paybackMonths(monthlyCents, totalCostCents) {
    if (monthlyCents <= 0)
        return null;
    return Math.ceil(totalCostCents / monthlyCents);
}
/**
 * Which devices the dispatch gate holds back for a terminal id.
 *
 * THE ONE PLACE this is decided. Fulfilment asks it per unit before calling
 * the merchant service, and the answer must be identical everywhere: a model
 * treated as a payment device on one screen and a peripheral on another
 * produces an order that is simultaneously blocked and shippable.
 *
 * `requiresTerminalId` wins when it is set — see the field's own note. With it
 * absent, only `payment_device` qualifies: a `pos_terminal` is an Android till
 * that may have no card path at all, and holding every one of them for a TID
 * would block software-only stores from ever receiving hardware.
 */
function needsTerminalId(model) {
    if (!model)
        return false;
    if (typeof model.requiresTerminalId === 'boolean')
        return model.requiresTerminalId;
    return model.category === 'payment_device';
}
/**
 * WHICH LANE A DEVICE BELONGS TO. The one place this is decided.
 *
 *   payment_device -> payment      (Payment devices)
 *   pos_terminal   -> pos          (POS devices)
 *   anything else  -> accessory    (Peripherals & accessories)
 *
 * The CATEGORY decides, and only the category. `needsTerminalId` above was
 * doing this job on the order screen, and it answers a different question — it
 * is the DISPATCH GATE ("must this unit be keyed before it ships"), overridable
 * per model with `requiresTerminalId`. A till with a card path set that flag and
 * was thereby filed under Payment devices. Two questions must not share one
 * answer; that is the whole reason `needsTerminalId` has this note of its own.
 *
 * `accessory` is a CATCH-ALL, deliberately: `office_supply`,
 * `marketing_material`, `tools` and any category added to the catalogue later
 * all belong to "Peripherals & accessories", so every device on an order is
 * drawn exactly once. A model with no category at all answers `null` — with
 * nothing to route on, it is left off every lane rather than guessed onto one.
 */
function deviceLane(model) {
    const category = model?.category;
    if (typeof category !== 'string' || !category)
        return null;
    if (category === 'payment_device')
        return 'payment';
    if (category === 'pos_terminal')
        return 'pos';
    return 'accessory';
}
/**
 * IS THIS CATALOG MODEL A SUNMI T3? (T3 card setup, 2026-10-14)
 *
 * @deprecated for gating — use `isCardSetupModel` (cardReaders.ts), which keys
 * on the model's `cardSetupPolicy`. Kept as a descriptive helper only.
 *
 * Decided by `appProfile.installProfileId === 'sunmi_t3'` — the installer
 * hardware profile a person set on the model — NEVER by the display name,
 * which is free text and changes. Accepts any partial row (a list item, a raw
 * DynamoDB item) so callers need not parse first. Null/undefined ⇒ false.
 */
exports.SUNMI_T3_INSTALL_PROFILE_ID = 'sunmi_t3';
function isSunmiT3Model(model) {
    return !!model && model.appProfile?.installProfileId === exports.SUNMI_T3_INSTALL_PROFILE_ID;
}
//# sourceMappingURL=hardwareCatalog.js.map